import * as React from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, DropdownOption } from '@ixicare/ui';
import { LogOut, Menu, User } from 'lucide-react';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import ImpersonationBanner from '~components/ImpersonationBanner/ImpersonationBanner';
import { DutyControlContainer, ViewOnlyChip } from '~components/Presence';
import MutedIndicator from './MutedIndicator';
import RequireHcpGroupModal from './RequireHcpGroupModal';
import GoToDutySiteButton from './GoToDutySiteButton';
import SiteSelector from './SiteSelector';
import TopBarOverflow from './TopBarOverflow';
import overflow from './TopBarOverflow.module.css';
import { useLanguageDropdownEntry } from './useLanguageDropdownEntry';
import { useDutyState } from '~hooks/useDutyState';
import { useObserverModeSync } from '~hooks/useObserverModeSync';
import { useMediaQuery } from '~hooks/useMediaQuery';
import styles from './TopBar.module.css';

/** Below this the bar can't seat every control at once — see TopBarOverflow.
 *  Same step as the rest of the compact layout (src/styles/variables.css). */
const COMPACT_QUERY = '(max-width: 1180px)';
/** Below this even the survivors have to give something up: the account menu
 *  drops to its icon. Its name is the longest unbreakable run left in the bar,
 *  and the menu behind it is unchanged — only its label goes. */
const NARROW_QUERY = '(max-width: 820px)';

/**
 * Two axes, one row. The left says which site you're *viewing*; the right says
 * whether you're *handling alarms*, for which group. They're independent —
 * browsing never changes duty — so nothing appears on both sides: the site
 * switcher never shows a group, and the duty control names its site only when
 * that isn't the one on screen.
 */
export interface TopBarProps {
  /** Supplied only while the nav is off-canvas: renders the burger that opens
   *  it. Without it the bar shows none — the nav is reachable on its own. */
  onOpenNav?: () => void;
  navOpen?: boolean;
}

const TopBar: React.FC<TopBarProps> = observer(({ onOpenNav, navOpen = false }) => {
  const navigate = useNavigate();
  const { auth, permissions, sites } = useStores();
  const { entry: languageEntry, modal: languageModal } = useLanguageDropdownEntry();
  const { isHcp, isOnDuty, isViewingDutySite, showReadOnlyTag, dutySiteUuid, dutySiteName, viewedSiteUuid } =
    useDutyState();
  const compact = useMediaQuery(COMPACT_QUERY);
  const narrow = useMediaQuery(NARROW_QUERY);
  useObserverModeSync();

  // Sites are loaded for every authenticated user (not just users with can_view_site),
  // so single-site HCPs also have sites.selectedSite populated. Site-scoped pages
  // (hcp-groups, configuration, missing devices, …) gate their queries on it.
  const userId = auth.user?.id;
  const userSiteUuid = auth.user?.site?.uuid;
  React.useEffect(() => {
    if (userId === undefined) return;
    sites.loadSites(userSiteUuid, userId);
  }, [sites, userId, userSiteUuid]);

  if (!auth.user) return null;

  // Sound follows duty, not what's on screen — you stay audible for your own
  // site's alarms while browsing another. HCPs keep it even before their site
  // resolves, since they're never off duty.
  const showNotificationIndicator = isHcp || isOnDuty;
  // The jump back only makes sense while on duty somewhere you aren't looking —
  // and only once we actually know where you're looking.
  const showJumpToDutySite = isOnDuty && !!viewedSiteUuid && !isViewingDutySite && !!dutySiteUuid;

  const dropDownEntries: DropdownOption[] = auth.isImpersonating
    ? [
        {
          label: t('user.actions.exitLoginAs'),
          lucide: LogOut,
          onClick: async () => {
            await auth.exitLoginAs();
            navigate('/healthcare-providers');
          },
        },
        languageEntry,
      ]
    : [
        {
          label: t('user.actions.view'),
          lucide: User,
          onClick: () => auth.user && navigate(`/healthcare-providers/${auth.user.id}/edit`),
        },
        languageEntry,
        {
          label: t('user.actions.logout'),
          lucide: LogOut,
          onClick: async () => {
            await auth.logout();
            navigate('/login');
          },
        },
      ];

  // States what you can do at the site you're looking at, rather than leaving
  // it implied by a duty pill naming a different one.
  const viewOnlyChip = showReadOnlyTag && <ViewOnlyChip dutySiteName={isOnDuty ? dutySiteName : undefined} />;
  const jumpToDutySite = showJumpToDutySite && <GoToDutySiteButton siteUuid={dutySiteUuid!} siteName={dutySiteName} />;
  const mutedIndicator = showNotificationIndicator && <MutedIndicator />;
  const hasSecondary = !!(viewOnlyChip || jumpToDutySite || mutedIndicator);
  const userName = `${auth.user.firstName} ${auth.user.lastName}`;

  return (
    <div className={styles.TopBar}>
      <div className={styles.LeftSlot}>
        {onOpenNav && (
          <button
            type="button"
            className={styles.NavToggle}
            onClick={onOpenNav}
            aria-label={t('global.openMenu')}
            title={t('global.openMenu')}
            aria-expanded={navOpen}
          >
            <Menu size={20} aria-hidden="true" />
          </button>
        )}
        {permissions.has('can_view_site') && <SiteSelector />}
        {!compact && viewOnlyChip}
      </div>

      <div className={styles.RightSlot}>
        <ImpersonationBanner />
        <DutyControlContainer />
        {!compact && jumpToDutySite}
        {!compact && mutedIndicator}
        {/* Narrow: everything but the two axes and the account menu folds away,
            so the bar never has to clip what it can't seat. */}
        {compact && hasSecondary && (
          <TopBarOverflow>
            {viewOnlyChip && <div className={overflow.Row}>{viewOnlyChip}</div>}
            {jumpToDutySite && <div className={overflow.Row}>{jumpToDutySite}</div>}
            {mutedIndicator && <div className={overflow.Row}>{mutedIndicator}</div>}
          </TopBarOverflow>
        )}
        <span className={styles.Divider} aria-hidden="true" />
        {narrow ? (
          <Dropdown lucide={User} options={dropDownEntries} aria-label={userName} title={userName} />
        ) : (
          <Dropdown name={userName} options={dropDownEntries} />
        )}
      </div>
      <RequireHcpGroupModal />
      {languageModal}
    </div>
  );
});

export default TopBar;
